import React, { useCallback, useContext, useEffect, useState } from 'react';
import { PRISMAX_BACKEND_URL } from '../../../configs/config';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { getApiErrorMessage, isSessionExpiredError } from '../../../utils/authErrors';
import SendEmailModal from '../DataSubscribers/shared/SendEmailModal/SendEmailModal';
import { downloadCsv, toUtcIso } from '../DataSubscribers/shared/downloadCsv';
import styles from '../VLAOperators/VLAAdminShared.module.css';

const PLATFORM_LABELS = {
    yam: 'YAM',
    piper: 'Piper',
    tok2: 'Tok2',
    realman: 'RealMan',
    unitree_g1: 'Unitree G1',
    other: 'Other',
};

const formatDate = (value) => value
    ? new Date(value).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    })
    : '—';

export default function RoboticsDataBetaApplications({ adminToken }) {
    const { handleAdminSessionExpired } = useContext(SwapMainAppContext);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sendingId, setSendingId] = useState(null);
    const [showSendAll, setShowSendAll] = useState(false);

    const approve = async (applicationId) => {
        setSendingId(applicationId);
        setError('');
        try {
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/admin/robotics-data-beta-applications/${applicationId}/review`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ decision: 'approve' }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                const message = getApiErrorMessage(result, 'Could not send approval email.');
                if (isSessionExpiredError(message)) handleAdminSessionExpired?.();
                throw new Error(message);
            }
            await loadApplications();
            if (!result.data?.email_sent) setError('Email delivery failed. Use Retry email to try again.');
        } catch (sendError) {
            setError(sendError.message || 'Could not send approval email.');
        } finally {
            setSendingId(null);
        }
    };

    const exportCsv = () => downloadCsv('beta-applications',
        ['Email', 'User ID', 'Requested At', 'Company', 'Role', 'Robot Platforms', 'Access Need', 'Approval Email Sent At'],
        applications.map((application) => [
            application.email || '',
            application.user_id,
            toUtcIso(application.applied_at),
            application.organization || '',
            application.role || '',
            (application.robot_platforms || []).map((platform) => PLATFORM_LABELS[platform] || platform).join('; '),
            application.access_need === 'custom' ? 'Custom request' : 'Self-serve',
            toUtcIso(application.approval_email_sent_at),
        ]));

    const loadApplications = useCallback(async () => {
        if (!adminToken) return;
        setLoading(true);
        setError('');
        try {
            const response = await fetch(
                `${PRISMAX_BACKEND_URL}/api/admin/robotics-data-beta-applications`,
                { headers: { Authorization: `Bearer ${adminToken}` } },
            );
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                const message = getApiErrorMessage(result, 'Failed to load applications.');
                if (isSessionExpiredError(message)) {
                    handleAdminSessionExpired?.();
                    return;
                }
                throw new Error(message);
            }
            setApplications(Array.isArray(result.data) ? result.data : []);
        } catch (loadError) {
            setError(loadError.message || 'Failed to load applications.');
        } finally {
            setLoading(false);
        }
    }, [adminToken, handleAdminSessionExpired]);

    useEffect(() => {
        void loadApplications();
    }, [loadApplications]);

    return (
        <section className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                    Beta Applications
                    <span className={styles.count}>{loading ? '…' : applications.length}</span>
                </h2>
                <button className={styles.collapseButton} type="button" onClick={loadApplications} disabled={loading}>
                    Refresh
                </button>
                <button className={styles.collapseButton} type="button" onClick={exportCsv}
                    disabled={loading || !applications.length}>
                    Download CSV
                </button>
                <button className={styles.approveButton} type="button" onClick={() => setShowSendAll(true)}>
                    Send email to all
                </button>
            </div>

            {error ? <div role="alert" className={styles.errorMsg}>{error}</div> : null}
            {loading ? <div className={styles.stateMsg}>Loading applications…</div> : null}
            {!loading && !error && !applications.length ? (
                <div className={styles.empty}>No interest registrations yet.</div>
            ) : null}

            {!loading && applications.length ? (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.th}>User</th>
                                <th className={styles.th}>Requested At</th>
                                <th className={styles.th}>Company / Role</th>
                                <th className={styles.th}>Robot Platforms</th>
                                <th className={styles.th}>Access Need</th>
                                <th className={styles.th}>Approval Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map((application) => {
                                return (
                                    <tr key={application.application_id} className={styles.row}>
                                        <td className={styles.cell}>
                                            <div className={styles.userCell}>
                                                <div className={styles.userPrimary}>{application.email || '—'}</div>
                                                <div className={styles.userId}>ID: {application.user_id}</div>
                                            </div>
                                        </td>
                                        <td className={styles.cell}>{formatDate(application.applied_at)}</td>
                                        <td className={styles.cell}>
                                            <div className={styles.userCell}>
                                                <div>{application.organization || '—'}</div>
                                                <div className={styles.userSecondary}>{application.role || 'No role provided'}</div>
                                            </div>
                                        </td>
                                        <td className={styles.cell}>
                                            <div className={styles.robotList}>
                                                {(application.robot_platforms || []).length
                                                    ? application.robot_platforms.map((platform) => (
                                                        <span key={platform} className={styles.robotChip}>
                                                            {PLATFORM_LABELS[platform] || platform}
                                                        </span>
                                                    ))
                                                    : <span>—</span>}
                                            </div>
                                        </td>
                                        <td className={styles.cell}>{application.access_need === 'custom' ? 'Custom request' : 'Self-serve'}</td>
                                        <td className={styles.cell}>
                                            {application.approval_email_sent_at ? (
                                                <span>Sent {formatDate(application.approval_email_sent_at)}</span>
                                            ) : (
                                                <button type="button" className={styles.approveButton}
                                                    disabled={sendingId !== null}
                                                    onClick={() => approve(application.application_id)}>
                                                    {sendingId === application.application_id ? 'Sending…'
                                                        : application.approval_email_error ? 'Retry email' : 'Approve & send email'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {showSendAll && (
                <SendEmailModal
                    title="Send email to all — Beta Applications"
                    note="Sending approves the selected applications and emails each applicant."
                    actionLabel="Approve & send"
                    adminToken={adminToken}
                    recipientsPath="/api/admin/robotics-data-beta-applications/email-recipients"
                    sendPath="/api/admin/robotics-data-beta-applications/approve-bulk"
                    onClose={() => setShowSendAll(false)}
                    onSent={loadApplications}
                />
            )}
        </section>
    );
}
